import { useNavigate, useParams } from 'react-router-dom'

import { Box, Grid, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { MoneyBag } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { InsightCard } from 'modules/project_management/components/InsightCard'

import BudgetStackedBarCard from './cards/BudgetStackedBarCard'
import CurrentProfitCard from './cards/CurrentProfitCard'
import BillableToDateTooltipMessage from './tooltips/BillableToDateTooltipMessage'
import CostTooltipMessage from './tooltips/CostTooltipMessage'

interface FinancialDetailsSectionProps {
  costToDate: number
  currency: string
  fixedCosts: number
  fixedCostPlannedIncome: number | undefined
  fixedCostPlannedCents: number | undefined
  hasPendingSpendings: boolean
  hoursPerBillRate: number
  hoursPerCostRate: number
  spendingCostsApprovedAmountCents: number
  timeAndMaterialsTotalBudget: number | undefined
}

const FinancialDetailsSection = ({
  costToDate,
  currency,
  fixedCosts,
  fixedCostPlannedIncome,
  fixedCostPlannedCents,
  hasPendingSpendings,
  hoursPerBillRate,
  hoursPerCostRate,
  spendingCostsApprovedAmountCents,
  timeAndMaterialsTotalBudget,
}: FinancialDetailsSectionProps) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [toggle, setToggle] = useToggle(false)
  const [pendingSpendingsBannerDismissed, setPendingSpendingsBannerDismissed] =
    useLocalStorage<boolean>(`hideProjectsPendingSpendingsBanner_${id}`)
  const {
    access: { locale },
  } = useCurrentSession()
  const tagTitle = fixedCostPlannedCents
    ? i18n.t('project_management.overview.sections.financial_details.fixed_price_type.tag')
    : timeAndMaterialsTotalBudget != null
    ? i18n.t('project_management.overview.sections.financial_details.time_and_materials_type.tag')
    : i18n.t('project_management.overview.sections.financial_details.no_budget_type.tag')

  const tagTooltipMessage = fixedCostPlannedCents
    ? i18n.t(
        'project_management.overview.sections.financial_details.fixed_price_type.tooltip_message'
      )
    : timeAndMaterialsTotalBudget != null
    ? i18n.t(
        'project_management.overview.sections.financial_details.time_and_materials_type.tooltip_message'
      )
    : i18n.t(
        'project_management.overview.sections.financial_details.no_budget_type.tooltip_message'
      )
  const revenueGridSize = fixedCostPlannedCents || timeAndMaterialsTotalBudget != null ? 6 : 4
  const costToDateGridSize = timeAndMaterialsTotalBudget != null ? 3 : 4
  const currentProfitGridSize = fixedCostPlannedCents
    ? 6
    : timeAndMaterialsTotalBudget != null
    ? 3
    : 4

  const usedBudget = hoursPerBillRate + spendingCostsApprovedAmountCents
  const registeredCosts = hoursPerCostRate + fixedCosts

  return (
    <Box gap="s20" paddingBottom="s8">
      <Box flexDirection="row" gap="s12">
        <Text size="300" weight="semibold" color="grey1000">
          {i18n.t('project_management.overview.sections.financial_details.title')}
        </Text>
        <Tooltip.ItemList
          attachment="bottom center"
          targetAttachment="top center"
          toggle={setToggle}
          open={toggle}
        >
          <Box cursor="pointer" paddingRight="s8">
            <F0TagRaw icon={MoneyBag} text={tagTitle} />
          </Box>
          <Text size="200" color="grey000White" weight="medium">
            {tagTooltipMessage}
          </Text>
        </Tooltip.ItemList>
      </Box>
      {hasPendingSpendings && !pendingSpendingsBannerDismissed && (
        <OneAlert
          title={i18n.t('project_management.overview.banners.pending_spendings.title')}
          description={''}
          variant={'warning'}
          action={{
            label: i18n.t('project_management.overview.banners.pending_spendings.link'),
            onClick: () => {
              setPendingSpendingsBannerDismissed(true)
              navigate(
                navigation.projects.id.details.spending.fullPath({
                  id: Number(id),
                })
              )
            },
          }}
        />
      )}
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, s: revenueGridSize }}>
          {fixedCostPlannedCents || timeAndMaterialsTotalBudget ? (
            <BudgetStackedBarCard
              currency={currency}
              current={fixedCostPlannedCents ? registeredCosts : usedBudget}
              currentLabel={
                timeAndMaterialsTotalBudget
                  ? `${i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
                    )}: ${MoneyWithCurrency({
                      amount: timeAndMaterialsTotalBudget,
                      currency,
                      locale,
                    })}`
                  : i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.fixed_price'
                    )
              }
              info={
                timeAndMaterialsTotalBudget ? (
                  <BillableToDateTooltipMessage
                    currency={currency}
                    hoursPerBillRate={hoursPerBillRate}
                    spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                  />
                ) : (
                  <CostTooltipMessage
                    currency={currency}
                    fixedCosts={fixedCosts}
                    hoursPerCostRate={hoursPerCostRate}
                    spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                    title={i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.tooltip_message.title_fixed_price'
                    )}
                  />
                )
              }
              locale={locale}
              planned={fixedCostPlannedCents || timeAndMaterialsTotalBudget || 0}
              plannedLabel={
                fixedCostPlannedCents
                  ? fixedCostPlannedCents - registeredCosts >= 0
                    ? i18n.t(
                        'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                        {
                          allocation: MoneyWithCurrency({
                            amount: fixedCostPlannedCents - registeredCosts,
                            currency,
                            locale,
                          }),
                        }
                      )
                    : i18n.t(
                        'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.fixed_price',
                        {
                          allocation: MoneyWithCurrency({
                            amount: fixedCostPlannedCents - registeredCosts,
                            currency,
                            locale,
                          }),
                        }
                      )
                  : timeAndMaterialsTotalBudget || 0 - usedBudget >= 0
                  ? i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                      {
                        allocation: MoneyWithCurrency({
                          amount: timeAndMaterialsTotalBudget || 0 - usedBudget,
                          currency,
                          locale,
                        }),
                      }
                    )
                  : i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.total_budget',
                      {
                        allocation: MoneyWithCurrency({
                          amount: timeAndMaterialsTotalBudget || 0 - usedBudget,
                          currency,
                          locale,
                        }),
                      }
                    )
              }
              showWarningTag={hasPendingSpendings && !!fixedCostPlannedCents}
              warningTooltipLabel={i18n.t(
                'project_management.overview.banners.pending_spendings.tooltip_message'
              )}
              title={
                fixedCostPlannedCents
                  ? i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.title_fixed_price'
                    )
                  : i18n.t(
                      'project_management.overview.sections.financial_details.insights.revenue_to_date.title_total_budget'
                    )
              }
            />
          ) : (
            <InsightCard.Cost
              totalCost={usedBudget}
              currency={currency}
              info={
                <BillableToDateTooltipMessage
                  currency={currency}
                  hoursPerBillRate={hoursPerBillRate}
                  spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                />
              }
              title={i18n.t(
                'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
              )}
            />
          )}
        </Box>
        {!fixedCostPlannedCents && (
          <Box gridColSpan={{ xxs: 12, s: costToDateGridSize }}>
            <InsightCard.Cost
              totalCost={costToDate + hoursPerCostRate}
              currency={currency}
              showWarningTag={hasPendingSpendings}
              warningTooltipLabel={i18n.t(
                'project_management.overview.banners.pending_spendings.tooltip_message'
              )}
              info={
                <CostTooltipMessage
                  currency={currency}
                  fixedCosts={fixedCosts}
                  hoursPerCostRate={hoursPerCostRate}
                  spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                  title={i18n.t(
                    'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                  )}
                />
              }
              title={i18n.t(
                'project_management.overview.sections.financial_details.insights.cost_to_date.title'
              )}
            />
          </Box>
        )}
        <Box gridColSpan={{ xxs: 12, s: currentProfitGridSize }}>
          <CurrentProfitCard
            billableToDate={usedBudget}
            costToDate={costToDate + hoursPerCostRate}
            currency={currency}
            fixedCostPlannedIncome={fixedCostPlannedIncome || 0}
            fixedCostPlannedCents={fixedCostPlannedCents}
            locale={locale}
            registeredCosts={registeredCosts}
          />
        </Box>
      </Grid>
    </Box>
  )
}

export default FinancialDetailsSection
